import { useCallback, useEffect, useRef, useState } from "react";
import type * as React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getPaymentLink } from "apis/v3/authApi";
import { MEMBERSHIP_ANNUAL_PRICE_USD } from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import { useMembershipActivationAfterPayment } from "hooks/useMembershipActivationAfterPayment";
import { usePaymentIframeEmbedGuard } from "hooks/usePaymentIframeEmbedGuard";
import { useUnaricPaypagePostMessage } from "hooks/useUnaricPaypagePostMessage";
import PageTitle from "utils/PageTitle";
import { trackPaymentCompleted, trackPaymentFailed } from "analytics";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import AccountExitLink from "components/v3/sharedComponents/AccountExitLink";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import {
  selectMembershipRenewalPayBlocked,
  setMembershipRenewalPayBlocked
} from "store/reducers/user/UserAccountSlice";
import { COLORS } from "components/v3/Theme/colors";

const LOADING_VIEWPORT_MAX_PX = 500;
const IFRAME_MIN_HEIGHT_PX = 850;

const IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX = 200;
const IFRAME_NARROW_MAX_WIDTH_CSS = "(max-width: 526px)";
const EXPAND_MAX_PX = 1600;

const IFRAME_ROUNDED_CORNER_RADIUS_PX = 10;
const IFRAME_ROUNDED_CORNER_PADDING_PX = 10;
const IFRAME_CHROME_VERTICAL_EXTRA_PX = IFRAME_ROUNDED_CORNER_PADDING_PX * 2;

const MembershipPaymentPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const renewalPayBlocked = useSelector(selectMembershipRenewalPayBlocked);
  const [ecommerceLink, setEcommerceLink] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const paymentIframeRef = useRef<HTMLIFrameElement>(null);
  const isNarrowPaymentViewport = useMediaQuery(IFRAME_NARROW_MAX_WIDTH_CSS, {
    defaultMatches: false
  });
  const iframeMinHeightLoaded =
    IFRAME_MIN_HEIGHT_PX +
    (isNarrowPaymentViewport ? IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX : 0);
  const outerMinHeightLoading =
    LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMinHeightLoaded =
    iframeMinHeightLoaded + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMaxHeightLoaded = EXPAND_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;

  const {
    beginActivationWait,
    showLongWaitCopy: showReactivationLongWaitCopy,
    waitingForActivation: waitingForReactivation
  } = useMembershipActivationAfterPayment({
    onActivated: () => {
      trackPaymentCompleted({ paymentType: "membership_renewal" });
      dispatch(setMembershipRenewalPayBlocked(false));
      history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_RECEIPT, {
        membershipRenewed: true,
        quantity: 1,
        totalUsd: MEMBERSHIP_ANNUAL_PRICE_USD
      });
    },
    onActivationTimeout: () => {
      trackPaymentFailed({
        paymentType: "membership_renewal",
        failureReason: "payment_accepted_account_not_reactivated"
      });
      dispatch(setMembershipRenewalPayBlocked(true));
      history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION, {
        membershipReactivationFailed: true
      });
    }
  });

  useEffect(() => {
    setIframeLoaded(false);
  }, [ecommerceLink]);

  useEffect(() => {
    if (!renewalPayBlocked) return;
    history.replace(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
  }, [history, renewalPayBlocked]);

  const handlePaymentEmbedFailed = useCallback(() => {
    dispatch(
      openGLSnackbar({
        variant: "error",
        header: "",
        subText:
          "The payment form couldn’t be loaded. Please try again and reach out to support if the problem persists."
      })
    );
    history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
  }, [dispatch, history]);

  usePaymentIframeEmbedGuard({
    ecommerceLink,
    iframeLoaded,
    enabled: Boolean(ecommerceLink) && !waitingForReactivation,
    onEmbedFailed: handlePaymentEmbedFailed
  });

  const handlePaymentIframeComplete = useCallback(() => {
    beginActivationWait();
  }, [beginActivationWait]);

  useUnaricPaypagePostMessage({
    iframeSrc: ecommerceLink,
    enabled: Boolean(ecommerceLink) && !waitingForReactivation,
    paymentIframeRef,
    handlers: {
      onSuccessScreen: handlePaymentIframeComplete,
      onComplete: handlePaymentIframeComplete,
      onCancel: () => {
        dispatch(
          openGLSnackbar({
            variant: "info",
            header: "",
            subText: "Payment was cancelled."
          })
        );
        history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
      },
      onErrorWithMessage: (message) => {
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "",
            subText: message
              ? `There was a problem processing your payment: ${message}`
              : "We couldn’t process your payment. You can try again or contact support."
          })
        );
      },
      onErrorScreen: () => {
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "",
            subText:
              "There was a problem with your payment. You can try again or contact support."
          })
        );
      },
      onErrorFlowClosed: () => {
        history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
      },
      onDirectDebitDialog: () => {
        globalThis.scrollTo(0, 0);
      }
    }
  });

  useEffect(() => {
    if (renewalPayBlocked) return;

    const handleLoadFailure = () => {
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "",
          subText: "Something went wrong, please try again."
        })
      );
      history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
    };

    getPaymentLink()
      .then((response) => {
        const link =
          response && "data" in response && response.data?.data?.ecommerceLink;
        if (link) {
          setEcommerceLink(link);
        } else {
          handleLoadFailure();
        }
      })
      .catch(() => {
        handleLoadFailure();
      });
  }, [dispatch, history, renewalPayBlocked]);

  PageTitle("Continue your membership");

  if (renewalPayBlocked) {
    return (
      <GradientFramedLayout showHeader showNav={false}>
        <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            marginX: "auto",
            maxWidth: { md: 740, xs: "100%" },
            minWidth: 0,
            paddingTop: { md: 3, xs: 2 },
            paddingX: { md: 3, xs: 2 },
            width: "100%"
          }}
        >
          <AccountExitLink to={PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION} />
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              maxHeight: LOADING_VIEWPORT_MAX_PX,
              minHeight: 360,
              mx: "auto"
            }}
          >
            <LoadingAnimation />
          </Box>
        </Box>
      </GradientFramedLayout>
    );
  }

  if (!ecommerceLink) {
    return (
      <GradientFramedLayout showHeader showNav={false}>
        <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            marginX: "auto",
            maxWidth: { md: 740, xs: "100%" },
            minWidth: 0,
            paddingTop: { md: 3, xs: 2 },
            paddingX: { md: 3, xs: 2 },
            width: "100%"
          }}
        >
          <AccountExitLink to={PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION} />
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              maxHeight: LOADING_VIEWPORT_MAX_PX,
              minHeight: 360,
              mx: "auto"
            }}
          >
            <LoadingAnimation />
          </Box>
        </Box>
      </GradientFramedLayout>
    );
  }

  if (waitingForReactivation) {
    return (
      <GradientFramedLayout showHeader showNav={false}>
        <Box
          sx={{
            alignItems: "center",
            alignSelf: "stretch",
            boxSizing: "border-box",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 0,
            px: { md: 3, xs: 2 },
            py: { md: 4, xs: 3 },
            width: "100%"
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              alignSelf: "stretch",
              backgroundColor: COLORS.WHITE,
              borderRadius: "16px",
              boxShadow: "0 16px 40px rgba(17, 24, 39, 0.08)",
              boxSizing: "border-box",
              display: "flex",
              flex: "1 1 auto",
              flexDirection: "column",
              gap: 2.5,
              justifyContent: "center",
              maxHeight: { md: "min(100%, calc(100dvh - 5.5rem))", xs: "100%" },
              maxWidth: 520,
              minHeight: {
                md: "max(24rem, min(70dvh, calc(100dvh - 6rem)))",
                xs: "max(20rem, min(62dvh, calc(100dvh - 6.5rem)))"
              },
              mx: "auto",
              px: { md: 5, xs: 3 },
              py: { md: 5, xs: 4 },
              textAlign: "center",
              width: "100%"
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%"
              }}
            >
              <LoadingAnimation />
            </Box>
            <Typography
              component="h1"
              variant="body_strong_xl_black"
              sx={{
                color: COLORS.BLACK,
                fontSize: { md: "1.25rem", xs: "1.125rem" },
                fontWeight: 700,
                lineHeight: 1.45,
                margin: 0
              }}
            >
              Please wait, we are reactivating your membership...
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: alpha(COLORS.DARK_GRAY_2, 0.95),
                lineHeight: 1.6,
                margin: 0,
                maxWidth: 420,
                mx: "auto"
              }}
            >
              {showReactivationLongWaitCopy
                ? "Almost there, please bear with us..."
                : "To avoid issues, do not close this window or refresh this page."}
            </Typography>
          </Box>
        </Box>
      </GradientFramedLayout>
    );
  }

  return (
    <GradientFramedLayout showHeader showNav={false}>
      <Box
        sx={{
          alignItems: "stretch",
          alignSelf: "stretch",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          marginX: "auto",
          maxWidth: { md: 740, xs: "100%" },
          minWidth: 0,
          paddingBottom: "40px",
          paddingTop: { md: 3, xs: 2 },
          paddingX: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <AccountExitLink to={PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION} />

        <Typography
          variant="h4_primary"
          component="h1"
          sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
        >
          Continue your membership
        </Typography>
        <Box
          sx={{
            position: "relative",
            boxSizing: "border-box",
            maxHeight: iframeLoaded
              ? outerMaxHeightLoaded
              : LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX,
            maxWidth: "100%",
            minHeight: iframeLoaded
              ? outerMinHeightLoaded
              : outerMinHeightLoading,
            minWidth: 0,
            mx: "auto",
            overflow: iframeLoaded ? "visible" : "hidden",
            py: { xs: 2, md: 3 },
            px: 0,
            transition: (theme) =>
              theme.transitions.create(["max-height", "min-height"], {
                duration: 450,
                easing: theme.transitions.easing.easeInOut
              }),
            width: "100%"
          }}
        >
          <Box
            sx={{
              backgroundColor: COLORS.WHITE,
              borderRadius: `${IFRAME_ROUNDED_CORNER_RADIUS_PX}px`,
              boxSizing: "border-box",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              p: `${IFRAME_ROUNDED_CORNER_PADDING_PX}px`,
              width: "100%"
            }}
          >
            <iframe
              ref={paymentIframeRef}
              key={ecommerceLink}
              title="Membership payment"
              src={ecommerceLink}
              onError={handlePaymentEmbedFailed}
              onLoad={() => setIframeLoaded(true)}
              style={{
                border: "none",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                maxWidth: "100%",
                minHeight: iframeLoaded
                  ? iframeMinHeightLoaded
                  : LOADING_VIEWPORT_MAX_PX,
                minWidth: 0,
                opacity: iframeLoaded ? 1 : 0,
                transition:
                  "min-height 450ms cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease-in",
                width: "100%"
              }}
            />
          </Box>
          {!iframeLoaded && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "transparent",
                p: { xs: 2, md: 3 }
              }}
            >
              <LoadingAnimation />
            </Box>
          )}
        </Box>
        <Typography
          variant="body_dark_gray_2"
          sx={{
            color: COLORS.DARK_GRAY,
            lineHeight: 1.65,
            mb: 2,
            textAlign: "center",
            fontSize: "13px"
          }}
        >
          Having issues reactivating your membership? Reach out to our team and
          we’ll get you sorted 1-on-1.
          <br />
          Renewing using a gift membership is not currently possible online—but
          our team would be happy to help.
        </Typography>
      </Box>
    </GradientFramedLayout>
  );
};

export default MembershipPaymentPage;
